/**
 * The preview, pointed at something real.
 *
 * Two sources, one simulator. A paired screen is previewed from the manifest the
 * API builds for it — the actual document the stick will fetch — so the preview
 * inherits every decision the builder made, including the ones that remove
 * things: a playlist whose assets have all expired is missing here for the same
 * reason it will be missing on the wall. A playlist still being edited is
 * previewed from a manifest assembled in the browser, because it does not exist
 * on the server yet, and it borrows the chosen screen's rotation and resolution
 * so the shape on screen is the shape on the wall.
 *
 * The clock is scrubbable in both directions from now, which is what makes a
 * daypart checkable on the wrong day of the week.
 */

import { useEffect, useMemo, useState } from 'react';
import type { ReactElement } from 'react';
import { useQuery } from '@tanstack/react-query';
import { HEARTBEAT_SEC } from '@pbtv/shared';
import type { FleetScreen, PlaylistItem } from '@pbtv/shared';
import { ErrorBlock, InlineEmpty, LoadingBlock } from '../Feedback.js';
import { ScreenPreview } from './ScreenPreview.js';
import { ClockScrubber } from './ClockScrubber.js';
import { DEFAULT_PREVIEW_RESOLUTION, rotationLabel } from './geometry.js';
import type { MediaAsset } from '../../lib/media.js';
import {
  describeInstant,
  describeOffset,
  draftManifest,
  fetchScreenManifest,
  getPreviewScreen,
  previewScreenQueryKey,
  screenManifestQueryKey,
} from '../../lib/manifestPreview.js';
import type { PreviewScreen } from '../../lib/manifestPreview.js';
import { FLEET_QUERY_KEY, listFleet } from '../../lib/screens.js';
import { STORES_QUERY_KEY, listStores } from '../../lib/stores.js';
import { useNow } from '../../lib/useNow.js';

/** The zone a draft falls back to when no screen has been chosen to borrow one from. */
const FALLBACK_TIMEZONE = 'Pacific/Auckland';

/**
 * The playlist being edited, if there is one.
 *
 * Every member has to be referentially stable across renders — the manifest is
 * memoised on this object, and a manifest with a fresh identity on every render
 * would re-run the resolver on every render. The page holds `items` and `assets`
 * in memos for exactly that reason.
 */
export interface DraftPreviewSource {
  playlistId: string;
  playlistName: string;
  items: readonly PlaylistItem[];
  assets: readonly MediaAsset[];
  /** Which store's screens to prefer when picking one to borrow a shape from. */
  storeId: string | null;
}

export interface ScreenPreviewPanelProps {
  draft?: DraftPreviewSource | null;
}

type PreviewSource = 'screen' | 'draft';

export function ScreenPreviewPanel({ draft = null }: ScreenPreviewPanelProps): ReactElement {
  const now = useNow(1000);
  const [screenId, setScreenId] = useState<string | null>(null);
  const [source, setSource] = useState<PreviewSource>('draft');
  const [offsetMs, setOffsetMs] = useState(0);

  const fleet = useQuery({
    queryKey: FLEET_QUERY_KEY,
    queryFn: ({ signal }) => listFleet(signal),
  });

  const stores = useQuery({
    queryKey: STORES_QUERY_KEY,
    queryFn: ({ signal }) => listStores(signal),
  });

  const rows = useMemo(() => sortScreens(fleet.data ?? []), [fleet.data]);

  // Choose a screen as soon as there is one, and let go of one that has been
  // un-paired underneath us rather than holding an id nothing answers to.
  useEffect(() => {
    if (rows.length === 0) return;
    if (screenId !== null && rows.some((row) => row.screen.id === screenId)) return;

    const wanted = draft === null ? null : draft.storeId;
    const preferred = wanted === null ? undefined : rows.find((row) => row.storeId === wanted);
    setScreenId((preferred ?? rows[0])?.screen.id ?? null);
  }, [rows, screenId, draft]);

  const previewScreen = useQuery({
    queryKey: previewScreenQueryKey(screenId ?? ''),
    queryFn: ({ signal }) => getPreviewScreen(screenId ?? '', signal),
    enabled: screenId !== null,
  });

  const effectiveSource: PreviewSource = draft === null ? 'screen' : source;
  const wantsLiveManifest = effectiveSource === 'screen' && screenId !== null;

  const manifest = useQuery({
    queryKey: screenManifestQueryKey(screenId ?? ''),
    queryFn: ({ signal }) => fetchScreenManifest(screenId ?? '', signal),
    enabled: wantsLiveManifest,
    // The device rebuilds its picture of the world once a heartbeat; a preview
    // left open on a second monitor should not be staler than the TV it mirrors.
    refetchInterval: HEARTBEAT_SEC * 1000,
  });

  const shape = previewScreen.data ?? null;
  const resolution = shape?.resolution ?? DEFAULT_PREVIEW_RESOLUTION;

  const timezone = useMemo(() => {
    const storeId = shape?.storeId ?? null;
    if (storeId === null) return FALLBACK_TIMEZONE;
    return stores.data?.find((store) => store.storeId === storeId)?.timezone ?? FALLBACK_TIMEZONE;
  }, [shape?.storeId, stores.data]);

  const draftPreview = useMemo(() => {
    if (draft === null) return null;
    return draftManifest({
      screen: {
        id: shape?.screenId ?? 'preview',
        name: shape?.name ?? 'Preview',
        rotationDegrees: shape?.rotationDegrees ?? 0,
      },
      timezone,
      playlistId: draft.playlistId,
      playlistName: draft.playlistName,
      items: draft.items,
      assets: draft.assets,
      nowMs: Date.now(),
    });
  }, [draft, shape?.screenId, shape?.name, shape?.rotationDegrees, timezone]);

  const shown = effectiveSource === 'draft' ? draftPreview : (manifest.data ?? null);
  const simulatedMs = now + offsetMs;
  const drift = describeOffset(offsetMs);

  return (
    <section className="panel">
      <div className="panel__head">
        <div>
          <h3 className="panel__title">Preview</h3>
          <p className="panel__subtitle">
            The same resolver the TV app runs, over the same manifest, on the same fifteen-second
            cadence. What plays, in what order and for how long is decided here exactly as it will
            be decided on the stick.
          </p>
        </div>
      </div>

      <div className="control-row">
        <label className="field field--inline">
          <span className="field__label">Screen</span>
          <select
            className="input"
            value={screenId ?? ''}
            disabled={rows.length === 0}
            onChange={(event) => setScreenId(event.currentTarget.value || null)}
          >
            {rows.length === 0 ? <option value="">No screens paired</option> : null}
            {rows.map((row) => (
              <option key={row.screen.id} value={row.screen.id}>
                {row.storeName} · {row.screen.name}
              </option>
            ))}
          </select>
        </label>

        {draft !== null ? (
          <div className="field field--inline">
            <span className="field__label">Showing</span>
            <div className="segmented" role="group" aria-label="What to preview">
              <button
                type="button"
                className={source === 'draft' ? 'segmented__item is-selected' : 'segmented__item'}
                aria-pressed={source === 'draft'}
                onClick={() => setSource('draft')}
              >
                This playlist
              </button>
              <button
                type="button"
                className={source === 'screen' ? 'segmented__item is-selected' : 'segmented__item'}
                aria-pressed={source === 'screen'}
                onClick={() => setSource('screen')}
                disabled={screenId === null}
              >
                What the screen plays
              </button>
            </div>
          </div>
        ) : null}
      </div>

      {effectiveSource === 'screen' ? (
        <>
          <ClockScrubber timezone={timezone} offsetMs={offsetMs} onChange={setOffsetMs} />
          <p className="panel__note">
            Simulated store clock: <strong>{describeInstant(simulatedMs, timezone)}</strong> ({timezone}
            ){drift === null ? '' : ` — ${drift}`}. Schedules, opening hours and overrides are all
            decided against this, so moving it is the honest way to check a daypart.
          </p>
        </>
      ) : null}

      {rows.length === 0 && effectiveSource === 'screen' ? (
        <InlineEmpty
          title="No screen to preview yet"
          description="A screen joins the fleet when a TV shows a pairing code and somebody claims it. Until then there is no rotation, no reported resolution and no manifest to play."
        />
      ) : null}

      {wantsLiveManifest && manifest.isPending ? (
        <LoadingBlock label="Fetching this screen's manifest…" />
      ) : null}

      {wantsLiveManifest && manifest.isError ? (
        <ErrorBlock
          error={manifest.error}
          action="load this screen's manifest"
          onRetry={() => void manifest.refetch()}
        />
      ) : null}

      {previewScreen.isError ? (
        <ErrorBlock
          error={previewScreen.error}
          action="read this screen's rotation and resolution"
          onRetry={() => void previewScreen.refetch()}
        />
      ) : null}

      {shown !== null ? (
        <ScreenPreview
          manifest={shown}
          resolution={resolution}
          clockOffsetMs={effectiveSource === 'draft' ? 0 : offsetMs}
          caption={caption(effectiveSource, shape, draft)}
        />
      ) : null}
    </section>
  );
}

function sortScreens(rows: readonly FleetScreen[]): FleetScreen[] {
  return [...rows].sort(
    (left, right) =>
      left.storeName.localeCompare(right.storeName) ||
      left.screen.name.localeCompare(right.screen.name),
  );
}

/**
 * What this frame is and is not. The draft caption says out loud that hours and
 * dayparts are absent, because "playing" here must not be read as a promise that
 * the playlist will be playing at any given moment on a real screen.
 */
function caption(
  source: PreviewSource,
  shape: PreviewScreen | null,
  draft: DraftPreviewSource | null,
): string {
  const shapeText =
    shape === null
      ? 'No screen chosen, so this is a plain landscape 1080p TV.'
      : `Shaped like ${shape.name}: ${rotationLabel(shape.rotationDegrees).toLowerCase()}, ${
          shape.resolutionReported
            ? 'at the resolution that screen reported'
            : 'at 1920 × 1080 — that screen has never reported its own'
        }.`;

  if (source === 'draft' && draft !== null) {
    return `${draft.playlistName}, as edited and not yet saved. ${shapeText} Opening hours, dayparts and overrides belong to a screen, so none are applied here — this is the loop itself, start to finish.`;
  }

  return `Everything this screen is configured to do. ${shapeText}`;
}
